

import { Action } from '@ngrx/store';

import { ReportSort } from '../reports/models';
import {
  CompanySettings,
  CropData,
  CropFieldAdvice,
  CustomerProgress,
  Employee,
  EnvironmentalImpactReport,
  GrasslandUsage,
  PlanetProofReport,
  SkalReport,
  SoilType,
} from '../shared/models';

export enum AppActionTypes {
  GetCropFieldAdvices = '[App] Get crop field advices',
  UpdateGrasslandUsages = '[App] Update grassland usages',
  GetCompanySettings = '[App] Get company settings',
  SetCompanySettings = '[App] Set company settings',
  GetSoilTypes = '[App] Get soil types',
  SetSoilTypes = '[App] Set soil types',
  SetCropFieldAdvices = '[App] Set crop field advices',
  OpenPlan = '[App] Open plan',
  OpenOrderPlanning = '[App] Open order planning',
  OpenFertilizerOrderPlanning = '[App] Open fertilizer order planning',
  ShowIndex = '[App] Show index',
  GetCrops = '[App] Get crops',
  SetCrops = '[App] Set crops',
  GetNonProductiveCrops = '[App] Get non productive crops',
  SetNonProductiveCrops = '[App] Set non productive crops',
  SetCropYear = '[App] Set crop year',
  GetDefaultCropYear = '[App] Get default crop year',
  SetDefaultCropYear = '[App] Set default crop year',
  CropYearChanged = '[App] Crop year changed',
  ContextChanged = '[App] Context changed',
  LoadCustomerData = '[App] Load customer data',
  SetAvailableCropYears = '[App] Set available crop year',
  GetLoggedInEmployeeData = '[App] Get logged in employee data',
  SetLoggedInEmployeeData = '[App] Set logged in employee data',
  GetSkalReport = '[App] Get skal report',
  SetSkalReport = '[App] Set skal report',
  GetFertilizationTableReport = '[App] Get fertilization table report',
  GetOrganicMatterReport = '[App] Get organic matter report',
  GetEnvironmentalImpactReport = '[App] Get environmental impact report',
  SetEnvironmentalImpactReport = '[App] Set environmental impact report',
  GetPlanetProofReport = '[App] Get planet proof report',
  SetPlanetProofReport = '[App] Set planet proof report',
  GetReportSort = '[App] Get report sort',
  SetReportSort = '[App] Set report sort',
  ToggleReportSortOption = '[App] Toggle report sort option',
  ToggleReportSortDirection = '[App] Toggle report sort direction',
  LoadCustomerProgress = '[App] Load customer progress',
  LoadCustomerProgressSuccess = '[App] Load customer progress success',
  ToggleCustomerProgressGBM = '[App] Toggle customer progress GBM',
  ToggleCustomerProgressFertilization = '[App] Toggle customer progress fertilization',
  EditAllFertilizationSettings = '[App] Edit all fertilization settings',
  ResetCropFieldSyncStatus = '[App] Reset crop field sync status',
  CropFieldDeleted = '[Overview][API] Crop field deleted',
  SendCancellationMail = '[App][API] Send cancellation mail',
  ShowGrainRegistrationReminder = '[App] Show grain registration reminder',
  DisableGrainRegistrationReminder = '[App] Disable grain registration reminder',
}

export class GetCropFieldAdvices implements Action {
  readonly type = AppActionTypes.GetCropFieldAdvices;
}

export class UpdateGrasslandUsages implements Action {
  readonly type = AppActionTypes.UpdateGrasslandUsages;
  constructor(public readonly grasslandUsage: GrasslandUsage) {}
}

export class GetCompanySettings implements Action {
  readonly type = AppActionTypes.GetCompanySettings;
}

export class SetCompanySettings implements Action {
  readonly type = AppActionTypes.SetCompanySettings;
  constructor(public readonly companySettings: CompanySettings) {}
}

export class GetSoilTypes implements Action {
  readonly type = AppActionTypes.GetSoilTypes;
}

export class SetSoilTypes implements Action {
  readonly type = AppActionTypes.SetSoilTypes;
  constructor(public readonly soilTypes: SoilType[]) {}
}

export class SetCropFieldAdvices implements Action {
  readonly type = AppActionTypes.SetCropFieldAdvices;
  constructor(public readonly cropFieldAdvices: CropFieldAdvice[]) {}
}

export class OpenPlan implements Action {
  readonly type = AppActionTypes.OpenPlan;
}

export class OpenOrderPlanning implements Action {
  readonly type = AppActionTypes.OpenOrderPlanning;
}

export class OpenFertilizerOrderPlanning implements Action {
  readonly type = AppActionTypes.OpenFertilizerOrderPlanning;
}

export class ResetCropFieldSyncStatus implements Action {
  readonly type = AppActionTypes.ResetCropFieldSyncStatus;
}

export class ShowIndex implements Action {
  readonly type = AppActionTypes.ShowIndex;
}

export class GetCrops implements Action {
  readonly type = AppActionTypes.GetCrops;
}

export class SetCrops implements Action {
  readonly type = AppActionTypes.SetCrops;
  constructor(public readonly crops: CropData[]) {}
}

export class GetNonProductiveCrops implements Action {
  readonly type = AppActionTypes.GetNonProductiveCrops;
}

export class SetNonProductiveCrops implements Action {
  readonly type = AppActionTypes.SetNonProductiveCrops;
  constructor(public readonly nonProductiveCrops: CropData[]) {}
}

export class GetDefaultCropYear implements Action {
  readonly type = AppActionTypes.GetDefaultCropYear;
}

export class SetDefaultCropYear implements Action {
  readonly type = AppActionTypes.SetDefaultCropYear;
  constructor(public readonly cropYear: number) {}
}

export class SetCropYear implements Action {
  readonly type = AppActionTypes.SetCropYear;
  constructor(public readonly cropYear: number) {}
}

export class CropYearChanged implements Action {
  readonly type = AppActionTypes.CropYearChanged;
  constructor(public readonly cropYear: number) {}
}

export class ContextChanged implements Action {
  readonly type = AppActionTypes.ContextChanged;
}

export class LoadCustomerData implements Action {
  readonly type = AppActionTypes.LoadCustomerData;
}

export class SetAvailableCropYears implements Action {
  readonly type = AppActionTypes.SetAvailableCropYears;
  constructor(public readonly availableCropYears: number[]) {}
}

export class GetLoggedInEmployeeData implements Action {
  readonly type = AppActionTypes.GetLoggedInEmployeeData;
}

export class SetLoggedInEmployeeData implements Action {
  readonly type = AppActionTypes.SetLoggedInEmployeeData;
  constructor(public readonly loggedInEmployee: Employee) {}
}

export class GetSkalReport implements Action {
  readonly type = AppActionTypes.GetSkalReport;
}

export class SetSkalReport implements Action {
  readonly type = AppActionTypes.SetSkalReport;
  constructor(public readonly skalReport: SkalReport) {}
}

export class GetFertilizationTableReport implements Action {
  readonly type = AppActionTypes.GetFertilizationTableReport;
  constructor(public readonly adviceIds: number[], public readonly cropFieldIds: number[]) {}
}

export class GetOrganicMatterReport implements Action {
  readonly type = AppActionTypes.GetOrganicMatterReport;
  constructor(public readonly cropFieldIds: number[]) {}
}

export class GetEnvironmentalImpactReport implements Action {
  readonly type = AppActionTypes.GetEnvironmentalImpactReport;
  constructor(public readonly cropFieldIds: number[]) {}
}

export class SetEnvironmentalImpactReport implements Action {
  readonly type = AppActionTypes.SetEnvironmentalImpactReport;
  constructor(public readonly environmentalImpactReport: EnvironmentalImpactReport) {}
}
export class GetPlanetProofReport implements Action {
  readonly type = AppActionTypes.GetPlanetProofReport;
  constructor(public readonly cropYear: number, public readonly cropFieldIds: number[]) {}
}

export class SetPlanetProofReport implements Action {
  readonly type = AppActionTypes.SetPlanetProofReport;
  constructor(public readonly planetProofReport: PlanetProofReport) {}
}

export class GetReportSort implements Action {
  readonly type = AppActionTypes.GetReportSort;
}

export class SetReportSort implements Action {
  readonly type = AppActionTypes.SetReportSort;
  constructor(public readonly reportSort: ReportSort) {}
}

export class ToggleReportSortOption implements Action {
  readonly type = AppActionTypes.ToggleReportSortOption;
}

export class ToggleReportSortDirection implements Action {
  readonly type = AppActionTypes.ToggleReportSortDirection;
}

export class LoadCustomerProgress implements Action {
  readonly type = AppActionTypes.LoadCustomerProgress;
}

export class LoadCustomerProgressSuccess implements Action {
  readonly type = AppActionTypes.LoadCustomerProgressSuccess;
  constructor(public readonly customerProgress: CustomerProgress) {}
}

export class ToggleCustomerProgressGBM implements Action {
  readonly type = AppActionTypes.ToggleCustomerProgressGBM;
}

export class ToggleCustomerProgressFertilization implements Action {
  readonly type = AppActionTypes.ToggleCustomerProgressFertilization;
}

export class EditAllFertilizationSettings implements Action {
  readonly type = AppActionTypes.EditAllFertilizationSettings;
}

export class CropFieldDeleted implements Action {
  readonly type = AppActionTypes.CropFieldDeleted;
  constructor(public readonly cropFieldId: number){}
}

export class SendCancellationMail implements Action {
  readonly type = AppActionTypes.SendCancellationMail;
}

export class ShowGrainRegistrationReminder implements Action {
  readonly type = AppActionTypes.ShowGrainRegistrationReminder;
  constructor(public readonly year: number) {}
}

export class DisableGrainRegistrationReminder implements Action {
  readonly type = AppActionTypes.DisableGrainRegistrationReminder;
  constructor(public readonly year: number) {}
}

// export the combined type of all actions
export type AppActions =
  | GetCropFieldAdvices
  | GetCompanySettings
  | SetCropFieldAdvices
  | ShowIndex
  | OpenPlan
  | OpenOrderPlanning
  | OpenFertilizerOrderPlanning
  | GetCrops
  | SetCrops
  | GetNonProductiveCrops
  | SetNonProductiveCrops
  | SetCropYear
  | GetDefaultCropYear
  | SetDefaultCropYear
  | CropYearChanged
  | ContextChanged
  | SetAvailableCropYears
  | GetLoggedInEmployeeData
  | SetLoggedInEmployeeData
  | GetSkalReport
  | SetSkalReport
  | GetFertilizationTableReport
  | GetOrganicMatterReport
  | GetEnvironmentalImpactReport
  | SetEnvironmentalImpactReport
  | GetPlanetProofReport
  | SetPlanetProofReport
  | GetReportSort
  | SetReportSort
  | ToggleReportSortOption
  | ToggleReportSortDirection
  | LoadCustomerProgress
  | LoadCustomerProgressSuccess
  | ToggleCustomerProgressGBM
  | ToggleCustomerProgressFertilization
  | EditAllFertilizationSettings
  | SetCompanySettings
  | GetSoilTypes
  | SetSoilTypes
  | ResetCropFieldSyncStatus
  | CropFieldDeleted
  | SendCancellationMail
  | ShowGrainRegistrationReminder
  | DisableGrainRegistrationReminder;
